import React, { createContext, useContext, useState } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { colors } from '@alixpartners/ui-components';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { useEffect } from 'react';
import { getSpendOverTimeChart } from 'utils/apiRequests';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { renderToString } from 'react-dom/server';

const SpendOverTime = createContext();

const convertData = inputData => {
	const result = inputData.reduce((acc, item) => {
		const existingYear = acc.find(el => el.year === item.year);
		const monthIndex = item.month - 1;

		if (!existingYear) {
			const newYearObj = {
				year: item.year,
				values: Array.from(
					{ length: 12 },
					(_, index) => (index === monthIndex ? item.spend : null)
				),
			};
			acc.push(newYearObj);
		} else {
			existingYear.values[monthIndex] = item.spend;
		}

		return acc;
	}, []);

	return result;
};

const findMissingMonths = data => {
	const missingMonths = [];

	for (let i = 1; i < data.length; i++) {
		const currentYear = data[i].year;
		const currentMonth = data[i].month;

		const prevYear = data[i - 1].year;
		const prevMonth = data[i - 1].month;

		if (currentYear === prevYear && currentMonth - prevMonth > 1) {
			for (let j = prevMonth + 1; j < currentMonth; j++) {
				missingMonths.push({ year: currentYear, month: j });
			}
		}
	}

	return missingMonths;
};

export const SpendOverTimeProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			type: 'line',
			height: chartheight,
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: { text: '' },
		legend: {
			align: 'center',
			padding: 0,
		},
		xAxis: {
			categories: [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			],
			crosshair: true,
			lineColor: colors.black,
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 4)}...`;
							}
						}
					}
				},
				style: { ...FONTSTYLE },
			},
			gridLineWidth: 0,
		},
		yAxis: {
			min: 0,
			title: { text: '' },
			gridLineWidth: 0,
			labels: {
				formatter: function() {
					return getFormattedSpendWithSymbol(
						this.value,
						currencySymbol,
						hideDecimals
					).toUpperCase();
				},
				style: {
					...FONTSTYLE,
				},
			},
			lineColor: colors.black,
			lineWidth: 1,
		},
		tooltip: {
			shared: true,
			useHTML: true,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						{this.points.map(item => (
							<TooltipItem key={`${item.point.series.name}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>{item.point.series.name}</TooltipItemName>
								</span>
								<TooltipItemValue>
									({getFormattedSpendWithSymbol(
										item.point.y,
										currencySymbol,
										true
									).toUpperCase()})
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
		},

		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
		colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
	});

	const { currentSelectCategory, dateRangeData, isFetching } = useContext(
		CategoryManagementContext
	);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
					};
					getSpendOverTimeChart(payload)
						.then(response => {
							if (response.data.length > 0) {
								const missingMonths = findMissingMonths(
									response.data.sort(
										(a, b) => a.year - b.year || a.month - b.month
									)
								);
								if (missingMonths.length > 0) {
									const newData = [
										...response.data,
										...findMissingMonths(
											response.data.sort(
												(a, b) => a.year - b.year || a.month - b.month
											)
										).map(item => ({ ...item, spend: 0 })),
									];
									setData(convertData(newData));
									setOptions(prevState => ({
										...prevState,
										series: convertData(newData).map(yearData => ({
											name: `${yearData.year}`,
											data: yearData.values,
										})),
									}));
								} else {
									setData(
										convertData(
											response.data.sort(
												(a, b) => a.year - b.year || a.month - b.month
											)
										)
									);
									setOptions(prevState => ({
										...prevState,
										series: convertData(
											response.data.sort(
												(a, b) => a.year - b.year || a.month - b.month
											)
										).map(yearData => ({
											name: `${yearData.year}`,
											data: yearData.values,
										})),
									}));
								}
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		},
		[
			dateRangeData,
			dateFrom,
			dateTo,
			categoryName,
			isSettingsFetching,
			isFetching,
		]
	);

	return (
		<SpendOverTime.Provider value={{ isLoading, data, options }}>
			{children}
		</SpendOverTime.Provider>
	);
};

export const useSpendOverTimeContext = () => {
	return useContext(SpendOverTime);
};
